/**
 * Small `Uint8Array` helpers so the core runs unchanged in the browser and in
 * Node — no `Buffer`, no Node globals. `atob`/`btoa`/`TextDecoder` are available
 * in both environments (Node ≥ 16/18 and browsers).
 */

/** Reads a big-endian unsigned 16-bit integer at {@link offset}. */
export function u16be(d: Uint8Array, offset: number): number {
  return ((d[offset]! << 8) | d[offset + 1]!) & 0xffff;
}

/** Reads a big-endian unsigned 32-bit integer at {@link offset}. */
export function u32be(d: Uint8Array, offset: number): number {
  return (
    d[offset]! * 0x1000000 +
    ((d[offset + 1]! << 16) | (d[offset + 2]! << 8) | d[offset + 3]!)
  );
}

/** Concatenates several byte arrays into one `Uint8Array`. */
export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const a of arrays) total += a.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

/** Lowercase hex encoding. */
export function toHex(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += x.toString(16).padStart(2, "0");
  return s;
}

/** Parses a hex string into bytes. */
export function fromHex(s: string): Uint8Array {
  const out = new Uint8Array(s.length >> 1);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Decodes bytes as a Latin-1 (ISO-8859-1) string — each byte maps to one code point. */
export function latin1Decode(b: Uint8Array): string {
  let s = "";
  // Chunk to avoid call-stack limits on large inputs.
  const CHUNK = 0x8000;
  for (let i = 0; i < b.length; i += CHUNK) {
    s += String.fromCharCode(...b.subarray(i, i + CHUNK));
  }
  return s;
}

/** Encodes a string to Latin-1 bytes (code points are masked to 8 bits). */
export function latin1Encode(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

/** Decodes standard base64 to bytes (isomorphic via `atob`). */
export function base64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Encodes bytes to standard base64 (isomorphic via `btoa`). */
export function bytesToBase64(b: Uint8Array): string {
  return btoa(latin1Decode(b));
}
